import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { mediaQueryLarge, requestIdleCallback, viewTransition } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';

/**
 * A custom element that renders a paginated list of items.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element.
 * @property {HTMLSpanElement} [viewMorePrevious] - The view more previous button.
 * @property {HTMLSpanElement} [viewMoreNext] - The view more next button.
 * @property {HTMLElement[]} [cards] - The cards elements.
 *
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  /**
   * @type {Map<number, string>}
   */
  pages = new Map();

  /** @type {IntersectionObserver | undefined} */
  infinityScrollObserver;

  /** @type {((value: void) => void) | null} */
  #resolveNextPagePromise = null;

  /** @type {((value: void) => void) | null} */
  #resolvePreviousPagePromise = null;

  /** @type {string | null} */
  #imageRatioSetting = null;

  /** @type {number} */
  #revealedCount = 0;

  connectedCallback() {
    super.connectedCallback();

    this.#storeImageRatioSettings();
    this.#initVirtualStream();

    this.#fetchPage('next');
    this.#fetchPage('previous');

    const { viewMoreNext } = this.refs;
    if (viewMoreNext && viewMoreNext.tagName === 'BUTTON') {
      this.#setupLoadMoreButton();
    } else {
      this.#observeViewMore();
    }

    this.#updateLoadMoreButtonVisibility();

    mediaQueryLarge.addEventListener('change', this.#handleViewportChange);
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  updatedCallback() {
    super.updatedCallback();
    this.#initVirtualStream();
    this.#updateLoadMoreButtonVisibility();
  }

  /**
   * Store the image ratio from the first product card for later use
   */
  #storeImageRatioSettings() {
    const firstCardGallery = this.querySelector('[ref="cardGallery"]');
    if (!firstCardGallery) return;

    this.#imageRatioSetting = firstCardGallery.getAttribute('data-image-ratio');
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
    }
    const { viewMoreNext } = this.refs;
    if (viewMoreNext && viewMoreNext.tagName === 'BUTTON') {
      viewMoreNext.removeEventListener('click', this.#handleLoadMoreClick);
    }
    mediaQueryLarge.removeEventListener('change', this.#handleViewportChange);
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  #handleViewportChange = () => {
    this.#initVirtualStream();
    this.#updateLoadMoreButtonVisibility();
  };

  #setupLoadMoreButton() {
    const { viewMoreNext } = this.refs;
    if (!viewMoreNext || viewMoreNext.tagName !== 'BUTTON') return;

    viewMoreNext.addEventListener('click', this.#handleLoadMoreClick);
  }

  #handleLoadMoreClick = async () => {
    const button = this.refs.viewMoreNext;
    if (button && button.tagName === 'BUTTON') {
      button.disabled = true;
    }
    await this.#renderNextPage();
    this.#updateLoadMoreButtonVisibility();
    if (button && button.tagName === 'BUTTON') {
      button.disabled = false;
    }
  };

  #updateLoadMoreButtonVisibility() {
    const wrapper = this.#getLoadMoreWrapper();
    if (!wrapper) return;

    wrapper.hidden = !this.#hasMoreToShow();
  }

  #getLoadMoreWrapper() {
    const { viewMoreNext } = this.refs;

    if (viewMoreNext instanceof HTMLElement) {
      const wrapper = viewMoreNext.closest('.main-collection-grid__load-more');
      if (wrapper instanceof HTMLElement) return wrapper;
    }

    const fallback = this.querySelector('.main-collection-grid__load-more');
    return fallback instanceof HTMLElement ? fallback : null;
  }

  #hasMoreToShow() {
    const { grid } = this.refs;
    if (!grid) return false;

    if (this.#usesDesktopVirtualStream()) {
      const streamLength = this.#getStreamItems().length;
      const hasHiddenInDom = this.#revealedCount < streamLength;
      const hasMoreServerPages = this.#getHighestServerPageInDom() < this.#getServerPageCount();
      return hasHiddenInDom || hasMoreServerPages;
    }

    if (this.#usesVirtualStream()) {
      return this.#getHighestServerPageInDom() < this.#getServerPageCount();
    }

    const lastPage = this.#getServerPageCount();
    const nextPage = this.#getPage('next');
    return nextPage && nextPage.page <= lastPage;
  }

  #observeViewMore() {
    const { viewMorePrevious, viewMoreNext } = this.refs;

    if (!viewMorePrevious || !viewMoreNext) return;

    this.infinityScrollObserver = new IntersectionObserver(
      async (entries) => {
        if (viewTransition.current) await viewTransition.current;

        for (const entry of entries) {
          if (entry.isIntersecting) {
            if (entry.target === viewMorePrevious) {
              this.#renderPreviousPage();
            } else {
              this.#renderNextPage();
            }
          }
        }
      },
      {
        rootMargin: '100px',
      }
    );

    this.infinityScrollObserver.observe(viewMorePrevious);
    this.infinityScrollObserver.observe(viewMoreNext);
  }

  /**
   * @param {Object} pageInfo - The page info
   * @param {number} pageInfo.page - The page number
   * @returns {boolean} Whether to use the page
   */
  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;
    if (pageInfo.page < 1) return false;

    return true;
  }

  /**
   * @param {"previous" | "next"} type
   */
  async #fetchPage(type) {
    const page = this.#getPage(type);

    if (!page || !this.#shouldUsePage(page)) return;

    await this.#fetchSpecificPage(page.page, page.url);

    if (type === 'next') {
      this.#resolveNextPagePromise?.();
      this.#resolveNextPagePromise = null;
    } else {
      this.#resolvePreviousPagePromise?.();
      this.#resolvePreviousPagePromise = null;
    }
  }

  /**
   * @param {number} pageNumber - The page number to fetch
   * @param {URL} [url] - Optional URL, will be constructed if not provided
   */
  async #fetchSpecificPage(pageNumber, url = undefined) {
    const pageInfo = { page: pageNumber, url };

    if (!url) {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set('page', pageNumber.toString());
      newUrl.hash = '';
      pageInfo.url = newUrl;
    }

    if (!this.#shouldUsePage(pageInfo)) return;
    const pageContent = await sectionRenderer.getSectionHTML(this.sectionId, true, pageInfo.url);
    this.pages.set(pageNumber, pageContent);
  }

  async #renderNextPage() {
    if (this.#usesDesktopVirtualStream()) {
      await this.#renderNextVirtualPage();
      return;
    }

    await this.#renderNextLegacyPage();
  }

  /**
   * Reveal the next 48 stream slots (promos + products), fetching server pages when needed.
   */
  async #renderNextVirtualPage() {
    const { grid } = this.refs;

    if (!grid) return;

    const virtualPageSize = this.#getVirtualPageSize();
    const serverPageCount = this.#getServerPageCount();
    const targetRevealed = this.#revealedCount + virtualPageSize;

    let streamItems = this.#getStreamItems();

    this.#revealedCount = Math.min(targetRevealed, streamItems.length);
    this.#applyVirtualVisibility();

    while (this.#revealedCount < targetRevealed && this.#getHighestServerPageInDom() < serverPageCount) {
      const nextPage = this.#getPage('next');
      if (!nextPage || !this.#shouldUsePage(nextPage)) break;

      let nextPageItemElements = this.#getGridItemsForPage(nextPage.page);

      if (!nextPageItemElements) {
        const promise = new Promise((res) => {
          this.#resolveNextPagePromise = res;
        });
        await promise;
        nextPageItemElements = this.#getGridItemsForPage(nextPage.page);
        if (!nextPageItemElements) break;
      }

      grid.append(...nextPageItemElements);
      this.#processNewElements();

      history.pushState('', '', nextPage.url.toString());

      streamItems = this.#getStreamItems();

      if (nextPage.page >= serverPageCount) {
        this.#revealedCount = streamItems.length;
        this.#applyVirtualVisibility();
        break;
      }

      this.#revealedCount = Math.min(targetRevealed, streamItems.length);
      this.#applyVirtualVisibility();
    }

    this.#updateLoadMoreButtonVisibility();

    requestIdleCallback(() => {
      this.#fetchPage('next');
    });
  }

  async #renderNextLegacyPage() {
    const { grid } = this.refs;

    if (!grid) return;

    const nextPage = this.#getPage('next');
    if (!nextPage || !this.#shouldUsePage(nextPage)) return;
    let nextPageItemElements = this.#getGridItemsForPage(nextPage.page);

    if (!nextPageItemElements) {
      const promise = new Promise((res) => {
        this.#resolveNextPagePromise = res;
      });
      await promise;
      nextPageItemElements = this.#getGridItemsForPage(nextPage.page);
      if (!nextPageItemElements) return;
    }

    grid.append(...nextPageItemElements);

    this.#processNewElements();

    history.pushState('', '', nextPage.url.toString());

    this.#updateLoadMoreButtonVisibility();

    requestIdleCallback(() => {
      this.#fetchPage('next');
    });
  }

  async #renderPreviousPage() {
    const { grid } = this.refs;

    if (!grid) return;

    const previousPage = this.#getPage('previous');
    if (!previousPage || !this.#shouldUsePage(previousPage)) return;

    let previousPageItemElements = this.#getGridItemsForPage(previousPage.page);
    if (!previousPageItemElements) {
      const promise = new Promise((res) => {
        this.#resolvePreviousPagePromise = res;
      });
      await promise;
      previousPageItemElements = this.#getGridItemsForPage(previousPage.page);
      if (!previousPageItemElements) return;
    }

    const scrollTop = window.scrollY;
    const firstElement = grid.firstElementChild;
    const oldHeight = firstElement ? firstElement.getBoundingClientRect().top + window.scrollY : 0;

    grid.prepend(...previousPageItemElements);

    this.#processNewElements();

    history.pushState('', '', previousPage.url.toString());

    if (firstElement) {
      const newHeight = firstElement.getBoundingClientRect().top + window.scrollY;
      const heightDiff = newHeight - oldHeight;
      window.scrollTo({
        top: scrollTop + heightDiff,
        behavior: 'instant',
      });
    }

    requestIdleCallback(() => {
      this.#fetchPage('previous');
    });
  }

  #initVirtualStream() {
    if (!this.#usesVirtualStream()) return;

    const streamLength = this.#getStreamItems().length;

    if (this.#usesDesktopVirtualStream()) {
      this.#revealedCount = Math.min(this.#getVirtualPageSize(), streamLength);
      this.#applyVirtualVisibility();
    } else {
      this.#revealedCount = streamLength;
      this.#clearVirtualVisibility();
    }

    this.#updateLoadMoreButtonVisibility();
  }

  #usesVirtualStream() {
    const { grid } = this.refs;
    return grid?.hasAttribute('data-virtual-page-size') ?? false;
  }

  /** Virtual 48-slot hide/show applies on desktop only; mobile shows the full grid per server page. */
  #usesDesktopVirtualStream() {
    return this.#usesVirtualStream() && mediaQueryLarge.matches;
  }

  #getVirtualPageSize() {
    const { grid } = this.refs;
    return Number(grid?.getAttribute('data-virtual-page-size')) || 48;
  }

  #getServerPageCount() {
    const { grid } = this.refs;
    return Number(grid?.getAttribute('last-page')) || 1;
  }

  #getStreamItems() {
    const { grid } = this.refs;
    if (!grid) return [];

    return [...grid.querySelectorAll(':scope > [data-stream-item]')];
  }

  /**
   * Product cards in the grid from live DOM (not refs — refs update async via MutationObserver).
   * @returns {HTMLElement[]}
   */
  #getProductCardsInGrid() {
    const { grid } = this.refs;
    if (!grid) return [];

    if (this.#usesVirtualStream()) {
      return [...grid.querySelectorAll(':scope > [data-item-type="product"]')].filter(
        (el) => el instanceof HTMLElement
      );
    }

    return [...grid.querySelectorAll(':scope > [ref="cards[]"]')].filter((el) => el instanceof HTMLElement);
  }

  #getHighestServerPageInDom() {
    const { grid } = this.refs;
    const productCards = this.#getProductCardsInGrid();

    if (productCards.length === 0) {
      return Number(grid?.getAttribute('data-server-page')) || 1;
    }

    const lastCard = productCards[productCards.length - 1];
    return Number(lastCard.dataset.serverPage || lastCard.dataset.page) || 1;
  }

  #applyVirtualVisibility() {
    if (!this.#usesDesktopVirtualStream()) {
      this.#clearVirtualVisibility();
      return;
    }

    const items = this.#getStreamItems();
    const limit = this.#revealedCount;

    for (const [index, item] of items.entries()) {
      const show = index < limit;
      item.classList.toggle('is-virtually-hidden', !show);
      item.toggleAttribute('aria-hidden', !show);
    }
  }

  #clearVirtualVisibility() {
    for (const item of this.#getStreamItems()) {
      item.classList.remove('is-virtually-hidden');
      item.removeAttribute('aria-hidden');
    }
  }

  /**
   * Process newly added elements and apply correct aspect ratios
   */
  #processNewElements() {
    requestAnimationFrame(() => {
      this.#imageRatioSetting === 'adapt' ? this.#fixAdaptiveAspectRatios() : this.#applyFixedAspectRatio();
    });
  }

  /**
   * Get all unprocessed card galleries
   * @returns {NodeListOf<Element>} List of unprocessed galleries
   */
  #getUnprocessedGalleries() {
    return this.querySelectorAll('.card-gallery:not([data-aspect-ratio-applied])');
  }

  /**
   * Mark gallery as processed
   * @param {HTMLElement} gallery - The gallery element to mark as processed
   */
  #markAsProcessed(gallery) {
    if (!(gallery instanceof HTMLElement)) return;
    gallery.setAttribute('data-aspect-ratio-applied', 'true');
  }

  /**
   * Calculate a safe aspect ratio value from image dimensions
   * @param {number} width - Natural width of the image
   * @param {number} height - Natural height of the image
   * @returns {string} Normalized aspect ratio as a string
   */
  #getSafeImageAspectRatio(width, height) {
    const rawRatio = width / height;
    return Math.max(0.1, Math.min(10, rawRatio)).toFixed(3);
  }

  /**
   * Apply an aspect ratio to a gallery and all its media containers
   * @param {HTMLElement} gallery - The gallery element
   * @param {string} aspectRatio - The aspect ratio to apply
   */
  #applyAspectRatioToGallery(gallery, aspectRatio) {
    if (!(gallery instanceof HTMLElement)) return;

    gallery.style.setProperty('--gallery-aspect-ratio', aspectRatio);

    const mediaContainers = gallery.querySelectorAll('.product-media-container');
    mediaContainers.forEach((container) => {
      if (container instanceof HTMLElement) {
        container.style.aspectRatio = aspectRatio;
      }
    });

    this.#markAsProcessed(gallery);
  }

  /**
   * Fix adaptive aspect ratios for newly added cards
   */
  #fixAdaptiveAspectRatios() {
    const newCardGalleries = this.#getUnprocessedGalleries();
    if (!newCardGalleries.length) return;

    const productRatioCache = new Map();

    newCardGalleries.forEach((gallery) => {
      if (!(gallery instanceof HTMLElement)) return;

      const productId = gallery.getAttribute('data-product-id');
      if (productId && productRatioCache.has(productId)) {
        this.#applyAspectRatioToGallery(gallery, productRatioCache.get(productId));
        return;
      }

      const img = gallery.querySelector('img');
      if (!img) {
        this.#applyAspectRatioToGallery(gallery, '1');
        return;
      }

      const loadAndSetRatio = () => {
        if (!img.naturalWidth || !img.naturalHeight) return;

        const imgRatio = this.#getSafeImageAspectRatio(img.naturalWidth, img.naturalHeight);

        if (productId) {
          productRatioCache.set(productId, imgRatio);
        }

        this.#applyAspectRatioToGallery(gallery, imgRatio);
      };

      if (img.complete) {
        loadAndSetRatio();
      } else {
        img.addEventListener('load', loadAndSetRatio, { once: true });
      }
    });
  }

  /**
   * Apply a fixed aspect ratio to all card-gallery and media container elements
   */
  #applyFixedAspectRatio() {
    if (!this.#imageRatioSetting) return;

    const aspectRatio = this.#getAspectRatioValue(this.#imageRatioSetting);
    if (!aspectRatio) return;

    const newCardGalleries = this.#getUnprocessedGalleries();
    if (!newCardGalleries.length) return;

    requestAnimationFrame(() => {
      newCardGalleries.forEach((gallery) => {
        if (!(gallery instanceof HTMLElement)) return;
        this.#applyAspectRatioToGallery(gallery, aspectRatio);
      });
    });
  }

  /**
   * Aspect ratio values matching the theme's standardized values
   * @type {Object.<string, string>}
   */
  static ASPECT_RATIOS = {
    square: '1',
    portrait: '0.8',
    landscape: '1.778',
  };

  /**
   * Get aspect ratio value based on setting
   * @param {string} ratioSetting - The ratio setting name
   * @returns {string|null} - The aspect ratio value or null
   */
  #getAspectRatioValue(ratioSetting) {
    return PaginatedList.ASPECT_RATIOS[ratioSetting] || null;
  }

  /**
   * @param {"previous" | "next"} type
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getPage(type) {
    const isPrevious = type === 'previous';
    const productCards = this.#getProductCardsInGrid();

    if (productCards.length === 0) return;

    const targetCard = productCards[isPrevious ? 0 : productCards.length - 1];
    const currentPage = Number(targetCard.dataset.page);

    if (!currentPage) return;

    const page = isPrevious ? currentPage - 1 : currentPage + 1;

    const url = new URL(window.location.href);
    url.searchParams.set('page', page.toString());
    url.hash = '';

    return {
      page,
      url,
    };
  }

  /**
   * @param {number} page
   * @returns {NodeListOf<Element> | undefined}
   */
  #getGridItemsForPage(page) {
    const pageHTML = this.pages.get(page);

    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const gridElement = parsedPage.querySelector('[ref="grid"]');
    if (!gridElement) return;

    const selector = this.#usesVirtualStream() ? ':scope > [data-stream-item]' : ':scope > [ref="cards[]"]';

    return gridElement.querySelectorAll(selector);
  }

  get sectionId() {
    const id = this.getAttribute('section-id');

    if (!id) throw new Error('The section-id attribute is required');

    return id;
  }

  /**
   * Handle filter updates by clearing cached pages
   */
  #handleFilterUpdate = () => {
    this.pages.clear();

    this.#resolveNextPagePromise?.();
    this.#resolvePreviousPagePromise?.();

    this.#resolveNextPagePromise = null;
    this.#resolvePreviousPagePromise = null;

    if (this.#usesVirtualStream()) {
      this.#revealedCount = this.#getVirtualPageSize();
    }

    this.#fetchSpecificPage(2);
  };
}
